import React from 'react';
import { GoogleMap, MarkerF, InfoWindowF, TrafficLayer, PolylineF } from '@react-google-maps/api';
import { FiPlus, FiMinus, FiRotateCcw, FiNavigation, FiMapPin } from 'react-icons/fi';
import { MdOutlineLiveTv, MdOutlineTraffic } from 'react-icons/md';
import { images } from '../../utils/images';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const mapOptions = {
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const FleetMap = ({
  isLoaded,
  center,
  zoom,
  isTracked,
  trafficMode,
  routesMode,
  liveMode,
  vehicles,
  selectedIds,
  activeMarker,
  setActiveMarker,
  handleZoomIn,
  handleZoomOut,
  handleReset,
  setLiveMode,
  setTrafficMode,
  setRoutesMode,
  onLoad,
  onUnmount,
  getMarkerIcon,
  navigate
}) => {
  const activeMapOptions = React.useMemo(() => ({
    ...mapOptions,
    gestureHandling: isTracked ? 'cooperative' : 'none',
    zoomControl: false,
    scrollwheel: isTracked,
    disableDoubleClickZoom: !isTracked,
    draggable: isTracked,
  }), [isTracked]);

  return (
    <div className="fleet_monitoring_map_panel">
      {/* Map Control Floating Panel */}
      <div className="fleet_map_controls_panel">
        <button 
          className="fleet_map_control_btn" 
          title="Zoom In"
          onClick={handleZoomIn}
          disabled={!isTracked}
        >
          <FiPlus size="18px" />
        </button>
        <button 
          className="fleet_map_control_btn" 
          title="Zoom Out"
          onClick={handleZoomOut}
          disabled={!isTracked}
        >
          <FiMinus size="18px" />
        </button>
        <div className="fleet_map_control_divider" />
        
        <button 
          className={`fleet_map_control_btn ${liveMode && isTracked ? 'active' : ''}`}
          title="Live Toggle"
          onClick={() => setLiveMode(prev => !prev)}
          disabled={!isTracked}
        >
          <MdOutlineLiveTv size="18px" />
        </button>

        <button 
          className={`fleet_map_control_btn ${trafficMode && isTracked ? 'active' : ''}`} 
          title="Traffic"
          onClick={() => setTrafficMode(prev => !prev)}
          disabled={!isTracked}
        >
          <MdOutlineTraffic size="18px" />
        </button>

        <button 
          className={`fleet_map_control_btn ${routesMode && isTracked ? 'active' : ''}`} 
          title="Routes"
          onClick={() => setRoutesMode(prev => !prev)}
          disabled={!isTracked}
        >
          <FiNavigation size="16px" />
        </button>

        <div className="fleet_map_control_divider" />
        <button 
          className="fleet_map_control_btn" 
          title="Reset Map"
          onClick={handleReset}
          disabled={!isTracked}
        >
          <FiRotateCcw size="16px" />
        </button>
      </div>

      {/* Map Overlay Instruction Card */}
      {!isTracked && (
        <div className="fleet_map_overlay_card">
          <div className="fleet_map_overlay_icon_wrapper">
            <FiMapPin size="24px" />
          </div>
          <h4>Select Vehicles to Start<br/>Live Tracking</h4>
          <p className="fleet_map_overlay_desc">
            Map API is not loaded until you select specific vehicles. This keeps the experience fast and cost-efficient.
          </p>
          <div className="fleet_map_overlay_steps">
            <div className="fleet_map_step_item">
              <span className="fleet_map_step_num">1</span>
              <p className="fleet_map_step_text">Check vehicles from the fleet list on the left</p>
            </div>
            <div className="fleet_map_step_item">
              <span className="fleet_map_step_num">2</span>
              <p className="fleet_map_step_text">Click "Track Selected" to load map & markers</p>
            </div>
            <div className="fleet_map_step_item">
              <span className="fleet_map_step_num">3</span>
              <p className="fleet_map_step_text">Click any marker to view vehicle logs & trip details</p>
            </div>
          </div>
        </div>
      )}

      {/* Google Map Pane */}
      {isLoaded ? (
        <div className={`fleet_map_wrapper ${!isTracked ? 'blurred' : ''}`}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={zoom}
            options={activeMapOptions}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onClick={() => setActiveMarker(null)}
          >
            {/* Live Traffic Overlay */}
            {isTracked && trafficMode && <TrafficLayer />}

            {/* Marker Pins */}
            {isTracked && vehicles
              .filter(v => selectedIds.includes(v.id))
              .map(v => (
                <React.Fragment key={v.id}>
                  <MarkerF
                    position={{ lat: v.lat, lng: v.lng }}
                    icon={getMarkerIcon(v.statusKey)}
                    onClick={() => setActiveMarker(v)}
                  />

                  {/* Planned Trip Route Polyline (full_polyline) - Blue */}
                  {routesMode && v.fullRoute && v.fullRoute.length > 1 && (
                    <PolylineF
                      path={v.fullRoute}
                      options={{
                        strokeColor: '#2196F3',
                        strokeOpacity: 0.6,
                        strokeWeight: 5,
                        clickable: false
                      }}
                    />
                  )}

                  {/* Traveled Cron Route Polyline (polyline) - Green / Red */}
                  {routesMode && v.traveledRoute && v.traveledRoute.length > 1 && (
                    <PolylineF
                      path={v.traveledRoute}
                      options={{
                        strokeColor: v.statusKey === 'stop' ? '#EB4747' : '#2E7D32',
                        strokeOpacity: 0.95,
                        strokeWeight: 6,
                        clickable: false
                      }}
                    />
                  )}

                  {/* Fallback Single Polyline Line if specific full/traveled routes not separate */}
                  {routesMode && (!v.fullRoute || v.fullRoute.length <= 1) && (!v.traveledRoute || v.traveledRoute.length <= 1) && v.route && v.route.length > 1 && (
                    <PolylineF
                      path={v.route}
                      options={{
                        strokeColor: v.statusKey === 'stop' ? '#EB4747' : '#2E7D32',
                        strokeOpacity: 0.85,
                        strokeWeight: 6,
                        clickable: false
                      }}
                    />
                  )}

                  {/* Start Point Marker (Green Flag/Pin) */}
                  {routesMode && v.startPoint && (
                    <MarkerF
                      position={v.startPoint}
                      title={`Start Point (${v.regNumber})`}
                      icon={{
                        url: images.startMap,
                        scaledSize: new window.google.maps.Size(32, 32),
                        anchor: new window.google.maps.Point(16, 32)
                      }}
                      zIndex={150}
                    />
                  )}

                  {/* End Point Marker (Red Flag/Pin) */}
                  {routesMode && v.endPoint && (
                    <MarkerF
                      position={v.endPoint}
                      title={`End Point (${v.regNumber})`}
                      icon={{
                        url: images.trip_end,
                        scaledSize: new window.google.maps.Size(32, 32),
                        anchor: new window.google.maps.Point(16, 32)
                      }}
                      zIndex={150}
                    />
                  )}
                </React.Fragment>
              ))
            }

            {/* Selected Marker Info Popup */}
            {isTracked && activeMarker && (
              <InfoWindowF
                position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
                onCloseClick={() => setActiveMarker(null)}
              >
                <div
                  className="fleet_info_window_container"
                  onClick={(e) => e?.stopPropagation?.()}
                >
                  <h6 className="fleet_info_window_title">
                    {activeMarker.regNumber}
                  </h6>
                  <p className="fleet_info_window_text">
                    <strong>Driver:</strong> {activeMarker.driver}
                  </p>
                  <p className="fleet_info_window_text">
                    <strong>Status:</strong> {activeMarker.status}
                  </p>
                  {activeMarker.location && activeMarker.location !== "-" && (
                    <p className="fleet_info_window_text">
                      <strong>Location:</strong> {activeMarker.location}
                    </p>
                  )}
                  <p className="fleet_info_window_text">
                    <strong>Speed:</strong> {activeMarker.speed}
                  </p>
                  <p className="fleet_info_window_text">
                    <strong>Fuel Remaining:</strong> {activeMarker.fuel}
                  </p>
                  <div className="fleet_info_window_footer">
                    <button 
                      className="fleet_info_window_btn"
                      onClick={() => navigate('/trips')}
                    >
                      View Trip logs
                    </button>
                  </div>
                </div>
              </InfoWindowF>
            )}

            {/* Polyline Route Legend Overlay */}
            {isTracked && routesMode && (
              <div style={{
                position: 'absolute',
                bottom: '24px',
                left: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                padding: '8px 14px',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#334155',
                zIndex: 10
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '16px', height: '4px', backgroundColor: '#2196F3', borderRadius: '2px', display: 'inline-block' }}></span>
                  <span>Planned Trip Route (`full_polyline`)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '16px', height: '4px', backgroundColor: '#2E7D32', borderRadius: '2px', display: 'inline-block' }}></span>
                  <span>Traveled Path (`polyline`)</span>
                </div>
              </div>
            )}
          </GoogleMap>
        </div>
      ) : (
        <div className="fleet_map_loading_state">
          Loading Map API...
        </div>
      )}
    </div>
  );
};

export default FleetMap;
